import React from 'react';
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { absoluteUrl } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, TrendingUp, Target, Settings, BarChart3, Zap } from 'lucide-react';
import dashboardImage from '@/assets/amazon-dashboard-cosmos-wave.png';

const ServicesAmazon = () => {
  const seoData = {
    title: "Services Amazon complets — Cosmos Wave | Ads, SEO, Operations",
    description: "Services Amazon clé en main : gestion Ads SP/SB/SD/DSP, SEO listings & A+, Stores, variations, prix, stocks. Croissance rentable mesurable avec SP-API.",
    breadcrumbs: [
      { name: "Accueil", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Amazon", url: "/services/amazon" }
    ],
    serviceSchema: {
      serviceType: "Services Amazon complets",
      areaServed: "France",
      description: "Gestion complète Amazon : Ads SP/SB/SD/DSP, SEO listings & A+, Stores, variations, prix, stocks. Méthode data-driven conforme SP-API."
    },
    faqSchema: [
      {
        question: "Que comprennent vos services Amazon ?",
        answer: "Gestion complète : Ads SP/SB/SD/DSP, SEO listings & A+ Content, Amazon Stores, variations produits, stratégie prix, gestion stocks. Approche data-driven avec SP-API."
      },
      {
        question: "Comment mesurez-vous les résultats ?",
        answer: "KPI précis : ROAS, ACoS, taux conversion, Buy Box %, CA généré, positions SEO, Health Score. Rapports détaillés et dashboard temps réel inclus."
      },
      {
        question: "Travaillez-vous avec tous les types de vendeurs ?",
        answer: "Oui : Vendor Central, Seller Central, FBA/FBM, Private Label, Wholesale, Retail Arbitrage. Stratégies adaptées selon votre modèle business."
      }
    ]
  };

  return (
    <>
      <AdvancedSEOHead 
        title={seoData.title}
        description={seoData.description}
        breadcrumbs={seoData.breadcrumbs}
        ogImage="/og/services-amazon-1200x630.jpg"
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": seoData.serviceSchema.serviceType,
            "description": seoData.serviceSchema.description,
            "areaServed": seoData.serviceSchema.areaServed,
            "provider": {
              "@type": "Organization",
              "name": "Cosmos Wave"
            }
          },
          {
            "@context": "https://schema.org", 
            "@type": "FAQPage",
            "mainEntity": seoData.faqSchema.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-orange-500/10 to-violet-600/20"></div>
          <div className="relative mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Services Amazon
                <span className="block text-2xl md:text-4xl text-orange-400 font-normal mt-2">
                  Ads, SEO & Operations
                </span>
              </h1>
              <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
                Gestion Amazon clé en main : Ads SP/SB/SD/DSP, SEO listings & A+, Stores, variations, prix, stocks. 
                Croissance rentable et Buy Box mesurable avec notre approche data-driven.
              </p>
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
                Demander un audit
              </Button>
            </div>
          </div>
        </section>

        {/* Dashboard Preview Section */}
        <section className="py-20 relative">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Solutions complètes pour optimiser votre présence et performance sur Amazon
              </h2>
              <p className="text-lg text-stone-400 max-w-3xl mx-auto">
                Dashboard intelligent, insights en temps réel et pilotage data-driven de vos opérations Amazon
              </p>
            </div>
            
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/30 via-orange-500/30 to-violet-600/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-50"></div>
              
              {/* Image container with glassmorphism */}
              <div className="relative rounded-2xl overflow-hidden border border-stone-700/50 backdrop-blur-sm bg-stone-900/30 p-2 shadow-2xl">
                <img 
                  src={dashboardImage} 
                  alt="Dashboard Amazon Cosmos Wave - Analytics et gestion produits" 
                  className="w-full h-auto rounded-xl transform group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              
              {/* Floating badges */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-4 flex-wrap justify-center">
                <div className="backdrop-blur-md bg-stone-900/80 border border-stone-700/50 px-6 py-3 rounded-full text-sm font-medium text-white shadow-lg">
                  📊 Analytics temps réel
                </div>
                <div className="backdrop-blur-md bg-stone-900/80 border border-stone-700/50 px-6 py-3 rounded-full text-sm font-medium text-white shadow-lg">
                  🎯 Multi-catégories
                </div>
                <div className="backdrop-blur-md bg-stone-900/80 border border-stone-700/50 px-6 py-3 rounded-full text-sm font-medium text-white shadow-lg">
                  📈 Growth tracking
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 pt-24">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Nos services Amazon</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <TrendingUp className="h-8 w-8" />,
                  title: "Amazon Ads (SP/SB/SD/DSP)",
                  items: [
                    "Campagnes Sponsored Products",
                    "Sponsored Brands & Display", 
                    "Amazon DSP programmatique",
                    "Optimisation enchères & ROAS"
                  ]
                },
                {
                  icon: <Target className="h-8 w-8" />,
                  title: "SEO Listings & A+",
                  items: [
                    "Optimisation fiches produits",
                    "A+ Content & EBC",
                    "Amazon Stores design",
                    "Mots-clés & ranking"
                  ]
                },
                {
                  icon: <Settings className="h-8 w-8" />,
                  title: "Catalog Health & Buy Box",
                  items: [
                    "Variations & suppressions",
                    "Stratégie prix & marges",
                    "Gestion stocks FBA/FBM",
                    "Monitoring Buy Box"
                  ]
                },
                {
                  icon: <BarChart3 className="h-8 w-8" />,
                  title: "Analytics & Reporting",
                  items: [
                    "Dashboard temps réel",
                    "Rapports performance",
                    "Veille concurrence",
                    "ROI & profitabilité"
                  ]
                },
                {
                  icon: <Zap className="h-8 w-8" />,
                  title: "Expansion Marketplace",
                  items: [
                    "Multi-pays Europe",
                    "Localization produits", 
                    "Compliance locale",
                    "Stratégie cross-border"
                  ]
                },
                {
                  icon: <CheckCircle className="h-8 w-8" />,
                  title: "Support & Formation",
                  items: [
                    "Support prioritaire",
                    "Formation équipes",
                    "Best practices",
                    "Veille réglementaire"
                  ]
                }
              ].map((service, index) => (
                <Card key={index} className="p-6 bg-stone-800/50 border-stone-700">
                  <div className="text-orange-400 mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-stone-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Méthode Section */}
        <section className="py-16 bg-stone-900/50">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Notre méthode data-driven</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: "1",
                  title: "Audit SP-API",
                  description: "Analyse complète de votre compte Amazon via SP-API pour identifier les opportunités."
                },
                {
                  step: "2", 
                  title: "Stratégie personnalisée",
                  description: "Plan d'action sur-mesure selon vos objectifs : croissance, rentabilité, expansion."
                },
                {
                  step: "3",
                  title: "Implémentation",
                  description: "Mise en œuvre des optimisations Ads, SEO, catalogue avec suivi temps réel."
                },
                {
                  step: "4",
                  title: "Optimisation continue", 
                  description: "Monitoring quotidien, ajustements stratégiques, reporting détaillé mensuel."
                }
              ].map((phase, index) => (
                <Card key={index} className="p-6 text-center bg-stone-800/50 border-stone-700">
                  <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {phase.step}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{phase.title}</h3>
                  <p className="text-stone-300 text-sm">{phase.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-gradient-to-r from-orange-600/20 to-violet-600/20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Accélérez votre croissance Amazon</h2>
            <p className="text-xl text-stone-300 mb-8">
              Confiez-nous la gestion complète de votre Amazon. Ads optimisés, SEO listings performants, 
              Buy Box sécurisé et croissance rentable mesurable.
            </p>
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
              Demander un audit gratuit
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesAmazon;